import { useState, useContext, useEffect, useMemo } from "react";
import { Modal } from "antd";
import { AuthContext } from "context/AuthContext";
import { showToast } from "ui/components/common";
import { Popover } from "react-tiny-popover";
import { AlertDataType } from "lib/types";
import { SvgClose, SvgDateSelection, SvgLogo } from "ui/svgs";
import { softTitle, formatDatetimeRange } from "lib/utils";
import AlertConrolBar from "ui/alerts/alert-control-bar";
import { APIProvider } from "@vis.gl/react-google-maps";
import MapControl from "ui/dealer-focus/map-control";
import dayjs from "dayjs";
import SiteSelectPopover from "ui/components/site-select-popover";
import type { FilterType } from "ui/alerts/img-search/img-search-modal-control";
import { Button, Skeleton } from "antd";
import { getAlertByGpt } from "pages/dashboard/alerts/service";
import { Empty } from "antd";
import AlertPlayer from "ui/alerts/alert-player";
import {
  getDealerInfo,
  requestLicense,
  useService,
} from "pages/dashboard/alerts/service";
import MagicSearchInput from "pages/dashboard/homePage/component/magicSearchInput";
import AlertCard from "pages/dashboard/homePage/component/alertCard";
import CalendarTimeRange from "ui/components/calendarTimeRange";
import "./img-search-modal.scss";

export default function ImgSearchModal({
  isOpen,
  filter,
  img,
  s3Key,
  onRequestClose,
  handleBackToCropImg,
  onFilterChange,
}: {
  isOpen: boolean;
  filter: FilterType;
  img: Blob;
  s3Key: string;
  onRequestClose: () => void;
  handleBackToCropImg: () => void;
  onFilterChange: (filter: FilterType) => void;
}) {
  const { privileges } = useContext(AuthContext);
  const { getSnapShotByAlertObj } = useService();

  const [activeAlert, setActiveAlert] = useState<AlertDataType>();
  const [alertData, setAlertData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [dealerInfo, setDealerInfo] = useState(null);
  const [snapShotSrc, setSnapShotSrc] = useState(null);
  const [isDatePopoverOpen, setIsDatePopoverOpen] = useState(false);
  const [stagedDate, setStagedDate] = useState(
    filter?.startedAt
      ? formatDatetimeRange(filter?.startedAt, filter?.endedAt)
      : "",
  );
  const [datePickerValue, setDatePickerValue] = useState<number[]>([]);

  useEffect(() => {
    if (!activeAlert) return;
    setSnapShotSrc(null);
    if (!activeAlert?.video?.url) {
      getSnapShotByAlertObj(activeAlert)
        .then((data) => {
          setSnapShotSrc(URL.createObjectURL(data));
        })
        .catch((e) => console.error(e));
    }
  }, [activeAlert]);

  useEffect(() => {
    if (!privileges.includes("accessDealer")) return;
    getDealerInfo().then((data) => {
      if (data.status !== 200) {
        showToast(1, "Failed to fetch dealer info");
        return;
      } else {
        setDealerInfo(data.data.company);
      }
    });
  }, [privileges]);

  useEffect(() => {
    getAlertsWithParams();
  }, [filter]);

  const hasNextAlert = useMemo(() => {
    if (!activeAlert) return false;
    const index = alertData.findIndex((alert) => alert.id === activeAlert.id);
    return index < alertData.length - 1;
  }, [activeAlert, alertData]);

  const hasPreviousAlert = useMemo(() => {
    if (!activeAlert) return false;
    const index = alertData.findIndex((alert) => alert.id === activeAlert.id);
    return index > 0;
  }, [activeAlert, alertData]);

  const handleNextAlert = () => {
    if (!hasNextAlert) return;
    const index = alertData.findIndex((alert) => alert.id === activeAlert.id);
    setActiveAlert(alertData[index + 1]);
  };

  const handlePreviousAlert = () => {
    if (!hasPreviousAlert) return;
    const index = alertData.findIndex((alert) => alert.id === activeAlert.id);
    setActiveAlert(alertData[index - 1]);
  };

  async function getAlertsWithParams() {
    setAlertData([]);
    setIsLoading(true);
    const abortController = new AbortController();

    const sendFilter = {
      ...filter,
      version: "image_v1",
      withLicense: true,
    };
    const res = await getAlertByGpt(
      undefined,
      s3Key,
      undefined,
      true,
      sendFilter,
      abortController,
      "magicV2",
      ImgSearchModal.name + "Magic Search",
    );
    //@ts-ignore
    if (res.code === "ERR_CANCELED") {
      return;
    }
    console.log("res", res);
    setIsLoading(false);
    if (res?.status !== 200) {
      showToast(1, "Failed to fetch alerts");
    } else {
      setAlertData([...res.data.data]);
    }
  }

  const handleCancelDate = () => {
    setStagedDate(null);
    setIsDatePopoverOpen(false);
  };

  const handleOkDate = () => {
    const startDate = new Date(datePickerValue?.at(0));

    const endDate = new Date(datePickerValue?.at(1));

    onFilterChange({ ...filter, startedAt: startDate, endedAt: endDate });

    setIsDatePopoverOpen(false);

    const formattedDate = formatDatetimeRange(startDate, endDate);
    setStagedDate(formattedDate);
  };

  const handleSite = (siteIds) => {
    onFilterChange({ ...filter, siteIds: [...siteIds] });
  };

  const clearDate = () => {
    onFilterChange({ ...filter, startedAt: undefined, endedAt: undefined });

    setStagedDate(null); // Clear the stagedDate to reflect no selection
  };

  function sendRequestForLicense() {
    Modal.confirm({
      title: "Confirm License Request",
      content: "This will send license request email to dealer.",
      async onOk() {
        requestLicense(activeAlert.cameraId, activeAlert.siteId).then(
          (data) => {
            if (data.status === 200) {
              showToast(0, "License request sent to dealer successfully");
            }
          },
        );
      },
      okText: "Send",
      okButtonProps: {
        className: "primaryButton ",
      },
      cancelButtonProps: {
        className: "primaryButton bordered",
      },
    });
  }

  const imgBlobSrc = useMemo(() => {
    return img ? URL.createObjectURL(img) : null;
  }, [img]);

  console.log("ImgSearchModal", filter, img);

  return (
    <Modal
      className="imgSearchModal"
      open={isOpen}
      onCancel={onRequestClose}
      footer={null}
      title={
        <span className="title ml-6">Search Results for Selected Region</span>
      }
      width={1700}
      maskClosable={false}
      destroyOnClose={true}
      centered
    >
      <div className="flex h-[700px] max-h-[700px] mt-6">
        <div className="w-[400px] border-r border-neutral-200 max-w-[400px] flex-shrink-0 flex flex-col overflow-hidden">
          <div className="px-6">
            <MagicSearchInput
              searchType={"imgSearch"}
              dropdownDisabled={true}
              imgBlobSrc={imgBlobSrc}
              onImgClose={() => handleBackToCropImg()}
              styles={{ width: "100%" }}
            />
          </div>
          <div className="flex flex-wrap px-6 items-center gap-2 mt-2">
            <Popover
              isOpen={isDatePopoverOpen}
              positions={["top", "left", "right"]}
              onClickOutside={() => setIsDatePopoverOpen(false)}
              content={
                <div className="border rounded-xl shadow-xl bg-white w-[320px] p-4">
                  <CalendarTimeRange
                    onSectionChange={(s, e) => {
                      setDatePickerValue([s.valueOf(), e.valueOf()]);
                    }}
                    defaultDate={
                      filter?.startedAt
                        ? dayjs(filter?.startedAt)
                        : dayjs().startOf("day")
                    }
                    shifts={null}
                    children={null}
                  />

                  <div className="flex justify-end space-x-2 mt-4">
                    <button
                      className="px-4 py-2 rounded text-sm text-blue-700 hover:bg-gray-100"
                      onClick={handleCancelDate}
                    >
                      Cancel
                    </button>
                    <button
                      className="px-4 py-2 rounded text-sm text-blue-700 hover:bg-blue-200"
                      onClick={handleOkDate}
                    >
                      OK
                    </button>
                  </div>
                </div>
              }
            >
              <div className="h-[35px] flex items-center">
                {stagedDate ? (
                  <div className="flex gap-1 items-center rounded-xl bg-blue-100 px-3 py-2 h-[33px]">
                    <SvgDateSelection
                      width="20"
                      className="fill-blue-500 flex-shrink-0"
                    />
                    <span className="text-sm font-semibold text-left flex-grow text-nowrap">
                      {stagedDate}
                    </span>
                    <button onClick={clearDate}>
                      <SvgClose
                        width="13"
                        className="fill-neutral-800 hover:fill-neutral-700"
                      />
                    </button>
                  </div>
                ) : (
                  <button
                    className="flex items-center rounded-xl hover:bg-zinc-200 border border-neutral-400 px-3 py-2 w-[135px] h-[33px]"
                    onClick={() => setIsDatePopoverOpen(!isDatePopoverOpen)}
                  >
                    <SvgDateSelection width="20" className="fill-blue-500" />
                    <p className="text-sm font-semibold text-left ml-1">
                      Select Date
                    </p>
                  </button>
                )}
              </div>
            </Popover>
            <SiteSelectPopover
              onSitesChange={handleSite}
              selectedSites={filter?.siteIds}
              multiSelect={false}
            />
          </div>
          {isLoading && (
            <div className="mt-4 flex flex-col items-center px-6 gap-2">
              {/* <div
                className=" text-blue-500  inline-block h-6 w-6 animate-spin rounded-full border-2 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"
                role="status"
              ></div> */}
              <Skeleton active title={false} />
              <Skeleton active title={false} />
              <Skeleton active title={false} />
            </div>
          )}
          <div
            className="flex-grow max-h-full "
            style={{
              //maxHeight: "calc(100% - 95px)",
              overflowY: "auto",
              marginTop: "4px",
            }}
          >
            {/* {Object.entries(groupedAlerts).map(([date, alerts]) => (
              <AlertsByDate
                key={date}
                date={date}
                alerts={alerts}
                setVideoUrl={() => {}}
                activeAlert={activeAlert}
                setActiveAlert={(alert) => setActiveAlert(alert)}
              />
            ))} */}

            {alertData?.map((alert) => {
              return (
                <AlertCard
                  key={alert.id}
                  alert={alert}
                  onSelectAlert={(alert) => {
                    setActiveAlert(alert);
                  }}
                  active={alert.id === activeAlert?.id}
                />
              );
            })}
            {!isLoading && alertData?.length == 0 && (
              <div className="mt-6">
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="No matching alerts found"
                />
              </div>
            )}
          </div>
        </div>
        <div className="flex-grow flex flex-col justify-center h-full gap-4 overflow-hidden p-6">
          {!activeAlert ? (
            <p className="w-full h-full text-center flex items-center justify-center font-semibold text-xl">
              Please select an alert
            </p>
          ) : activeAlert?.camera?.hasLicense ? (
            <>
              {activeAlert?.snapshot?.location !== "local" &&
              activeAlert?.video?.url ? (
                <>
                  <div className="flex-grow h-full md:overflow-y-hidden flex items-center overflow-y-hidden justify-center">
                    <AlertPlayer
                      videoUrl={activeAlert?.video?.url}
                      from={"basic"}
                    />
                  </div>
                </>
              ) : (
                <div
                  className="flex-grow h-full md:overflow-y-hidden flex items-center overflow-y-hidden"
                  style={{ maxHeight: "calc(100% - 80px)" }}
                >
                  <div className="relative h-full aspect-video max-w-full max-h-full flex items-center justify-center">
                    {snapShotSrc ? (
                      <img
                        src={snapShotSrc}
                        alt="Camera Snapshot"
                        className="aspect-video rounded-lg w-full max-h-full"
                      />
                    ) : (
                      <div className="absolute top-0 left-0 w-full h-full flex flex-col items-center justify-center">
                        <SvgLogo width="80" className="fill-neutral-400" />
                        <p className="text-neutral-400 font-semibold text-2xl">
                          {softTitle()}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div
              style={{
                textAlign: "center",
                justifyContent: "center",
                width: "100%",
              }}
              className="flex-grow h-full md:overflow-y-hidden flex items-center overflow-y-hidden bg-zinc-100 rounded-2xl"
            >
              <div style={{ width: "684px" }}>
                <div style={{ fontWeight: "600", fontSize: "34px" }}>
                  Camera License Required
                </div>
                <div style={{ fontSize: "16px", marginTop: "12px" }}>
                  This camera is not licensed. Alert Clip and controls are
                  disabled until the license is activated.
                </div>
                <div
                  style={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "space-between",
                    marginTop: "16px",
                  }}
                >
                  <div
                    style={{
                      width: "330px",
                      height: "153px",
                      backgroundColor: "rgba(225, 225, 225, 1)",
                      borderRadius: "24px",
                      justifyItems: "center",
                      alignContent: "center",
                    }}
                  >
                    <div style={{ fontSize: "16px" }}>
                      Contact your dealer at
                    </div>
                    <div style={{ fontSize: "16px" }}>
                      {dealerInfo?.contactName || ""}
                    </div>
                    <div style={{ fontSize: "16px" }}>
                      {dealerInfo?.email || ""}
                    </div>
                    <div style={{ fontSize: "16px" }}>
                      {dealerInfo?.phone || ""}
                    </div>
                  </div>
                  <div
                    style={{
                      width: "330px",
                      height: "153px",
                      backgroundColor: "rgba(225, 225, 225, 1)",
                      borderRadius: "24px",
                      justifyItems: "center",
                      alignContent: "center",
                    }}
                  >
                    <div style={{ fontSize: "16px", width: "270px" }}>
                      Click “Request” to send a license request
                    </div>
                    <Button
                      disabled={!privileges.includes("accessDealer")}
                      className="primaryButton mt-2"
                      onClick={sendRequestForLicense}
                    >
                      Request
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
          <AlertConrolBar
            hasNextAlert={hasNextAlert}
            hasPreviousAlert={hasPreviousAlert}
            activeAlert={activeAlert}
            setActiveAlert={setActiveAlert}
            onNextAlert={handleNextAlert}
            onPreviousAlert={handlePreviousAlert}
            hiddenButtons={[
              "alertStatus",
              "merge",
              "gpt",
              "imgSearch",
              "addRecord",
              "contact",
              "note",
              "location",
            ]}
          />
        </div>
        {activeAlert && (
          <div className="w-[380px] rounded-2xl overflow-hidden border-r border-neutral-200 max-w-[380px] flex-shrink-0">
            <APIProvider apiKey={"AIzaSyAqk45RIVytV7tiTjTy2UISpHEgSWjl1vg"}>
              <MapControl
                siteId={activeAlert?.siteId}
                location={activeAlert?.location}
                eventType={activeAlert?.eventTypes[0] || ""}
              />
            </APIProvider>
            {/* <Map
              draggableCursor={"unset"}
              defaultZoom={4}
              defaultCenter={{ lat: 37.0902, lng: -95.7129 }}
              mapId="a060739f521f7239"
            >
              <PoiMarkers pois={locations} />
            </Map> */}
          </div>
        )}
      </div>
    </Modal>
  );
}
